import React, { useState, useRef } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Tooltip,
  Button,
  Chip,
  Typography,
  Paper,
  Divider
} from '@mui/material';
import {
  FormatListNumbered as FormatListNumberedIcon,
  Help as HelpIcon,
  AutoAwesome as MagicIcon,
  Code as CodeIcon
} from '@mui/icons-material';
import ListBuilderModal from './ListBuilderModal';

const EnhancedMessageInput = ({ 
  label = "Message Content",
  value = '', 
  onChange, 
  availableVariables = [],
  availableApiEndpoints = [], // Add API endpoints for field extraction
  placeholder = "Enter your message...",
  multiline = true,
  rows = 4,
  helperText,
  fullWidth = true,
  size = "small",
  disabled = false
}) => {
  const [showListBuilder, setShowListBuilder] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [showVariableHelper, setShowVariableHelper] = useState(false);
  const inputRef = useRef(null);
  
  // Detect available array variables
  const detectAvailableArrays = () => {
    return availableVariables.filter(variable => {
      return variable.type === 'array' || 
             variable.dataType === 'array' ||
             (variable.description && variable.description.toLowerCase().includes('array'));
    }).map(arrayVar => {
      console.log('🔍 [EnhancedMessageInput] Processing array variable:', arrayVar.name, arrayVar);
      
      // Try to find the source API endpoint for this array variable
      let sourceApiEndpoint = null;
      if (arrayVar.sourceStep && availableApiEndpoints.length > 0) {
        // This is a rough heuristic - in a real implementation, you'd want to track
        // which API endpoint each variable comes from more precisely
        console.log('🔍 [EnhancedMessageInput] Looking for API endpoint for sourceStep:', arrayVar.sourceStep);
        
        // For now, try to match by variable name patterns or source description
        if (arrayVar.name.includes('doctor') || arrayVar.description?.includes('doctor')) {
          sourceApiEndpoint = availableApiEndpoints.find(ep => 
            ep.name.toLowerCase().includes('doctor') || 
            ep.description?.toLowerCase().includes('doctor')
          )?._id;
        } else if (arrayVar.name.includes('schedule') || arrayVar.description?.includes('schedule')) {
          sourceApiEndpoint = availableApiEndpoints.find(ep => 
            ep.name.toLowerCase().includes('schedule') || 
            ep.description?.toLowerCase().includes('schedule')
          )?._id;
        } else if (arrayVar.name.includes('clinic') || arrayVar.description?.includes('clinic')) {
          sourceApiEndpoint = availableApiEndpoints.find(ep => 
            ep.name.toLowerCase().includes('clinic') || 
            ep.description?.toLowerCase().includes('clinic')
          )?._id;
        }
        
        console.log('🔍 [EnhancedMessageInput] Found source API endpoint:', sourceApiEndpoint);
      }
      
      return {
        name: arrayVar.name,
        description: arrayVar.description || `Array variable from ${arrayVar.source}`,
        source: arrayVar.source,
        sourceApiEndpoint, // Add the source API endpoint ID
        sampleData: generateSampleData(arrayVar)
      };
    });
  };

  // Generate sample data for preview (use real data if available, otherwise mock data)
  const generateSampleData = (variable) => {
    // If the variable has actual sample data, use it
    if (variable.sampleValue && Array.isArray(variable.sampleValue)) {
      return variable.sampleValue.slice(0, 2); // Use first 2 items
    }
    
    // Otherwise, generate mock data based on variable name
    const varName = variable.name.toLowerCase();
    
    if (varName.includes('doctor')) {
      return [
        {
          type: 'doctor-clinics',
          id: '207',
          attributes: {
            doctor: { id: 302, name: 'Dr. John Smith' },
            clinic: { id: 19, name: 'General Hospital' },
            speciality: { id: 32, name: 'Cardiology' }
          }
        },
        {
          type: 'doctor-clinics', 
          id: '208',
          attributes: {
            doctor: { id: 303, name: 'Dr. Sarah Johnson' },
            clinic: { id: 20, name: 'Medical Center' },
            speciality: { id: 33, name: 'Neurology' }
          }
        }
      ];
    } else if (varName.includes('appointment')) {
      return [
        {
          id: 'slot1',
          date: '2024-01-15',
          time: '09:00',
          duration: 30,
          available: true
        },
        {
          id: 'slot2',
          date: '2024-01-15', 
          time: '10:30',
          duration: 30,
          available: true
        }
      ];
    } else if (varName.includes('product')) {
      return [
        {
          name: 'Product A',
          price: 100,
          category: 'Electronics',
          stock: 5,
          rating: 4.5
        },
        {
          name: 'Product B',
          price: 200,
          category: 'Accessories',
          stock: 3,
          rating: 4.2
        }
      ];
    } else {
      // Generic structure
      return [
        {
          id: 1,
          name: 'Item 1',
          description: 'Sample description',
          value: 'Sample value'
        },
        {
          id: 2,
          name: 'Item 2', 
          description: 'Another description',
          value: 'Another value'
        }
      ];
    }
  };

  const availableArrays = detectAvailableArrays();
  
  const insertTemplate = (template) => {
    if (!inputRef.current) return;
    
    const input = inputRef.current;
    const start = input.selectionStart || cursorPosition;
    const end = input.selectionEnd || cursorPosition;
    
    const newValue = value.substring(0, start) + template + value.substring(end);
    onChange(newValue);
    
    // Reset cursor position after the inserted template
    setTimeout(() => {
      const newCursorPos = start + template.length;
      input.setSelectionRange(newCursorPos, newCursorPos);
      input.focus();
    }, 0);
    
    setShowListBuilder(false);
  };

  const insertVariable = (variableName) => {
    const template = `{{${variableName}}}`;
    insertTemplate(template);
  };

  const detectExistingArraySyntax = (text) => {
    const arrayFormatRegex = /\{\{([^[\]|]+)\[\*\]\|((?:[^{}]|\{[^}]*\})*)\}\}/g;
    return [...text.matchAll(arrayFormatRegex)];
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    // Always call onChange, even with empty strings to prevent reverting to defaults
    onChange(newValue);
    setCursorPosition(e.target.selectionStart);
  };

  const handleCursorPositionChange = (e) => {
    setCursorPosition(e.target.selectionStart);
  };

  const existingArraySyntax = detectExistingArraySyntax(value);

  return (
    <Box sx={{ position: 'relative' }}>
      <TextField
        inputRef={inputRef}
        label={label}
        value={value}
        onChange={handleInputChange}
        onSelect={handleCursorPositionChange}
        onBlur={(e) => setCursorPosition(e.target.selectionStart)}
        placeholder={placeholder}
        multiline={multiline}
        rows={rows}
        fullWidth={fullWidth}
        size={size}
        disabled={disabled}
        helperText={helperText}
        InputProps={{
          sx: { 
            paddingRight: availableArrays.length > 0 ? '100px' : '40px' 
          }
        }}
      />
      
      {/* Action Buttons */}
      <Box sx={{ 
        position: 'absolute', 
        right: 8, 
        top: 8,
        display: 'flex',
        gap: 0.5
      }}>
        {/* List Builder Button - only show if arrays are available */}
        {availableArrays.length > 0 && (
          <Tooltip title={`Format list from ${availableArrays.length} available array${availableArrays.length !== 1 ? 's' : ''}`}>
            <IconButton
              size="small"
              onClick={() => setShowListBuilder(true)}
              sx={{ 
                backgroundColor: 'rgba(25, 118, 210, 0.1)',
                color: '#1976d2',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.2)'
                }
              }}
            >
              <FormatListNumberedIcon />
            </IconButton>
          </Tooltip>
        )}
        
        {/* Variable Helper Button */}
        <Tooltip title={`Insert variables (${availableVariables.length} available)`}>
          <IconButton
            size="small"
            onClick={() => setShowVariableHelper(!showVariableHelper)}
            sx={{ 
              backgroundColor: showVariableHelper ? 'rgba(25, 118, 210, 0.2)' : 'rgba(0, 0, 0, 0.1)',
              color: showVariableHelper ? '#1976d2' : '#666',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.2)'
              }
            }}
          >
            <CodeIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Variable Helper Panel */}
      {showVariableHelper && (
        <Paper 
          sx={{ 
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            mt: 1,
            p: 2,
            zIndex: 1000,
            maxHeight: 200,
            overflow: 'auto',
            boxShadow: 3
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            💡 Available Variables
          </Typography>
          
          {availableVariables.length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {availableVariables.map((variable) => (
                <Chip
                  key={variable.name}
                  label={variable.name}
                  size="small"
                  variant="outlined"
                  onClick={() => insertVariable(variable.name)}
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.1)'
                    }
                  }}
                />
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary">
              No variables available yet. Create API call steps to generate variables.
            </Typography>
          )}
          
          <Divider sx={{ my: 1 }} />
          
          <Typography variant="caption" color="textSecondary">
            Click any variable to insert it. Use {'{{'} and {'}'} to wrap variable names.
          </Typography>
        </Paper>
      )}

      {/* Existing Array Syntax Indicator */}
      {existingArraySyntax.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="success.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <MagicIcon sx={{ fontSize: 16 }} />
            {existingArraySyntax.length} list format{existingArraySyntax.length !== 1 ? 's' : ''} detected
          </Typography>
        </Box>
      )}

      {/* Array Availability Hint */}
      {availableArrays.length > 0 && existingArraySyntax.length === 0 && (
        <Box sx={{ 
          mt: 1, 
          p: 1.5, 
          backgroundColor: '#e3f2fd', 
          borderRadius: 1,
          border: '1px solid #bbdefb'
        }}>
          <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#1565c0', fontWeight: 600 }}>
            💡 Pro Tip: You have {availableArrays.length} array variable{availableArrays.length !== 1 ? 's' : ''} available!
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.7rem', color: '#1976d2', mt: 0.5 }}>
            Click the <FormatListNumberedIcon sx={{ fontSize: 14, mx: 0.5, verticalAlign: 'middle' }} /> button to create beautiful lists automatically.
          </Typography>
        </Box>
      )}

      {/* List Builder Modal */}
      <ListBuilderModal
        open={showListBuilder}
        onClose={() => setShowListBuilder(false)}
        availableArrays={availableArrays}
        availableVariables={availableVariables}
        availableApiEndpoints={availableApiEndpoints}
        onInsertTemplate={insertTemplate}
      />
    </Box>
  );
};

export default EnhancedMessageInput; 